<div class="flex min-w-0 flex-auto flex-col">
  <header class="bg-card border-b">
    <div
      class="mx-auto flex w-full max-w-screen-xl flex-col gap-5 px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8"
    >
      <div class="flex min-w-0 items-center gap-4">
        <div
          class="overview-avatar flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full"
        >
          @if (companyLogo) {
            <img
              class="h-full w-full object-cover"
              [src]="companyLogo"
              (error)="onCompanyLogoError()"
              alt=""
            />
          } @else {
            <span aria-hidden="true">{{ companyInitial }}</span>
          }
        </div>
        <div class="min-w-0">
          <h1 class="text-2xl font-semibold leading-8 tracking-tight">
            Bookings Overview
          </h1>
          <p class="text-secondary mt-0.5 truncate text-sm">
            Welcome back, {{ displayName }} · {{ companyName }}
          </p>
        </div>
      </div>

      <div class="flex items-center justify-between gap-2 sm:justify-end">
        <span class="text-secondary hidden text-sm md:inline">{{
          todayLabel
        }}</span>
        <button mat-stroked-button type="button" (click)="viewAllBookings()">
          View bookings
        </button>
        <button
          mat-icon-button
          type="button"
          aria-label="Refresh dashboard"
          [disabled]="isDashboardLoading"
          (click)="refreshDashboard()"
        >
          <mat-icon [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
        </button>
      </div>
    </div>
  </header>

  <main class="flex-auto border-t">
    <div class="mx-auto w-full max-w-screen-xl px-6 py-6 sm:px-8 sm:py-8">
      <section
        class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
        aria-label="Booking summary"
      >
        @if (statsState === "loading") {
          @for (placeholder of [1, 2, 3, 4]; track placeholder) {
            <div class="overview-card overview-stat-card" role="status">
              <div class="overview-skeleton h-4 w-28"></div>
              <div class="overview-skeleton mt-5 h-10 w-36"></div>
              <div class="overview-skeleton mt-4 h-3 w-44"></div>
              <span class="sr-only">Loading booking summary</span>
            </div>
          }
        } @else if (statsState === "error") {
          <div
            class="overview-card overview-state sm:col-span-2 xl:col-span-4"
            role="alert"
          >
            <mat-icon
              [svgIcon]="'heroicons_outline:exclamation-triangle'"
            ></mat-icon>
            <div>
              <p class="font-medium">Booking summary could not be loaded.</p>
              <p class="text-secondary mt-1 text-sm">
                Try refreshing this section.
              </p>
            </div>
            <button mat-stroked-button type="button" (click)="loadStats()">
              Try again
            </button>
          </div>
        } @else {
          @for (card of summaryCards; track card.label) {
            <article class="overview-card overview-stat-card">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <h2 class="text-secondary text-sm font-medium">
                    {{ card.label }}
                  </h2>
                  <p
                    class="mt-3 truncate text-3xl font-semibold tracking-tight"
                  >
                    {{ card.value }}
                  </p>
                </div>
                <div
                  class="overview-stat-icon"
                  [class]="
                    'overview-stat-icon overview-stat-icon--' + card.tone
                  "
                  aria-hidden="true"
                >
                  <mat-icon [svgIcon]="card.icon"></mat-icon>
                </div>
              </div>
              <p class="text-secondary mt-4 text-sm">
                {{ card.supportingText }}
              </p>
              <p class="text-hint mt-1 text-xs">All-time</p>
            </article>
          }
        }
      </section>

      <section class="mt-6 grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-12">
        <article class="overview-card min-w-0 xl:col-span-8">
          <div class="overview-card-header">
            <div>
              <h2 class="text-lg font-medium leading-6 tracking-tight">
                Bookings over time
              </h2>
              <p class="text-secondary mt-1 text-sm">
                Previous 7 completed days
              </p>
            </div>
            @if (trendState === "error") {
              <button mat-stroked-button type="button" (click)="loadBarChart()">
                Retry
              </button>
            }
          </div>

          @if (trendState === "loading") {
            <div class="overview-chart-skeleton mt-6" role="status">
              <span class="sr-only">Loading booking trend</span>
            </div>
          } @else if (trendState === "error") {
            <div class="overview-empty-state" role="alert">
              <mat-icon
                [svgIcon]="'heroicons_outline:exclamation-triangle'"
              ></mat-icon>
              <p>Booking activity could not be loaded.</p>
            </div>
          } @else if (!hasBookingTrendData) {
            <div class="overview-empty-state">
              <mat-icon [svgIcon]="'heroicons_outline:chart-bar'"></mat-icon>
              <p>No booking activity was recorded during this period.</p>
            </div>
          } @else {
            <apx-chart
              class="mt-5 block h-80 w-full"
              [chart]="bookingsChartOptions.chart"
              [colors]="bookingsChartOptions.colors"
              [dataLabels]="bookingsChartOptions.dataLabels"
              [grid]="bookingsChartOptions.grid"
              [legend]="bookingsChartOptions.legend"
              [plotOptions]="bookingsChartOptions.plotOptions"
              [series]="bookingsSeries"
              [states]="bookingsChartOptions.states"
              [stroke]="bookingsChartOptions.stroke"
              [tooltip]="bookingsChartOptions.tooltip"
              [xaxis]="bookingsChartOptions.xaxis"
              [yaxis]="bookingsChartOptions.yaxis"
            ></apx-chart>
          }
        </article>

        <article class="overview-card min-w-0 xl:col-span-4">
          <div class="overview-card-header">
            <div>
              <h2 class="text-lg font-medium leading-6 tracking-tight">
                Booking status
              </h2>
              <p class="text-secondary mt-1 text-sm">All-time distribution</p>
            </div>
            @if (statusState === "error") {
              <button mat-stroked-button type="button" (click)="loadPieChart()">
                Retry
              </button>
            }
          </div>

          @if (statusState === "loading") {
            <div class="overview-donut-skeleton mx-auto mt-8" role="status">
              <span class="sr-only">Loading booking status</span>
            </div>
          } @else if (statusState === "error") {
            <div class="overview-empty-state" role="alert">
              <mat-icon
                [svgIcon]="'heroicons_outline:exclamation-triangle'"
              ></mat-icon>
              <p>Booking status could not be loaded.</p>
            </div>
          } @else if (bookingStatusRows.length === 0) {
            <div class="overview-empty-state">
              <mat-icon [svgIcon]="'heroicons_outline:chart-pie'"></mat-icon>
              <p>No booking status data is available yet.</p>
            </div>
          } @else {
            <div class="relative mx-auto mt-5 h-48 max-w-72">
              <apx-chart
                class="flex h-full w-full items-center justify-center"
                [chart]="bookingStatusChartOptions.chart"
                [colors]="bookingStatusChartOptions.colors"
                [labels]="bookingStatusChartOptions.labels"
                [plotOptions]="bookingStatusChartOptions.plotOptions"
                [series]="bookingStatusSeries"
                [states]="bookingStatusChartOptions.states"
                [tooltip]="bookingStatusChartOptions.tooltip"
              ></apx-chart>
              <div class="overview-donut-total" aria-hidden="true">
                <span class="text-2xl font-semibold">{{
                  bookingStatusTotal
                }}</span>
                <span class="text-secondary text-xs">Bookings</span>
              </div>
            </div>
            <ul class="mt-5 divide-y" aria-label="Booking status totals">
              @for (row of bookingStatusRows; track row.label) {
                <li
                  class="grid grid-cols-[1fr_auto_auto] items-center gap-3 py-2.5 text-sm"
                >
                  <span class="flex min-w-0 items-center gap-2">
                    <span
                      class="h-2.5 w-2.5 shrink-0 rounded-full"
                      [style.backgroundColor]="row.color"
                    ></span>
                    <span class="truncate">{{ row.label }}</span>
                  </span>
                  <span class="font-medium">{{ row.count }}</span>
                  <span class="text-secondary w-14 text-right"
                    >{{ row.percentage }}%</span
                  >
                </li>
              }
            </ul>
          }
        </article>
      </section>

      <section class="mt-6 grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-12">
        <app-court-occupancy-chart
          class="block h-full min-w-0 xl:col-span-8"
        ></app-court-occupancy-chart>

        <aside
          class="overview-card flex h-full min-w-0 flex-col xl:col-span-4"
          aria-labelledby="upcoming-title"
        >
          <div class="overview-card-header">
            <div>
              <h2
                id="upcoming-title"
                class="text-lg font-medium leading-6 tracking-tight"
              >
                Upcoming bookings
              </h2>
              <p class="text-secondary mt-1 text-sm">
                Next 7 days: next booking each day
              </p>
            </div>
            <button mat-button type="button" (click)="viewAllBookings()">
              View all
            </button>
          </div>

          @if (scheduleState === "loading") {
            <div class="mt-4 flex-auto space-y-3" role="status">
              @for (placeholder of [1, 2, 3]; track placeholder) {
                <div class="rounded-xl border p-4">
                  <div class="overview-skeleton h-4 w-32"></div>
                  <div class="overview-skeleton mt-3 h-3 w-44"></div>
                </div>
              }
              <span class="sr-only">Loading upcoming bookings</span>
            </div>
          } @else if (scheduleState === "error") {
            <div class="overview-empty-state flex-auto" role="alert">
              <mat-icon
                [svgIcon]="'heroicons_outline:exclamation-triangle'"
              ></mat-icon>
              <p>Upcoming bookings could not be loaded.</p>
              <button mat-stroked-button type="button" (click)="loadSchedule()">
                Try again
              </button>
            </div>
          } @else if (upcomingBookings.length === 0) {
            <div class="overview-empty-state flex-auto">
              <mat-icon [svgIcon]="'heroicons_outline:calendar'"></mat-icon>
              <p>No upcoming bookings in the next seven days.</p>
            </div>
          } @else {
            <div class="mt-4 flex-auto divide-y">
              @for (
                scheduleItem of upcomingBookings;
                track trackByBooking($index, scheduleItem)
              ) {
                <button
                  type="button"
                  class="overview-booking-row"
                  (click)="onBookingClick(scheduleItem)"
                >
                  <span class="flex min-w-0 items-start justify-between gap-3">
                    <span class="min-w-0 text-left">
                      <span class="block truncate font-medium">
                        {{ scheduleItem.title || "Booking" }}
                      </span>
                      <span
                        class="text-secondary mt-1 flex items-center text-sm"
                      >
                        <mat-icon
                          class="icon-size-4 mr-1.5"
                          [svgIcon]="'heroicons_solid:clock'"
                        ></mat-icon>
                        {{ formatProductDate(scheduleItem.start) }} ·
                        {{ scheduleItem.start | date: "h:mm a" }}–{{
                          scheduleItem.end | date: "h:mm a"
                        }}
                      </span>
                      @if (scheduleItem.location) {
                        <span
                          class="text-secondary mt-1 flex items-center text-sm"
                        >
                          <mat-icon
                            class="icon-size-4 mr-1.5"
                            [svgIcon]="'heroicons_solid:map-pin'"
                          ></mat-icon>
                          <span class="truncate">{{
                            scheduleItem.location
                          }}</span>
                        </span>
                      }
                    </span>
                    <span [class]="bookingStatusClass(scheduleItem.status)">
                      {{ formatStatusLabel(scheduleItem.status) }}
                    </span>
                  </span>
                </button>
              }
            </div>
          }
        </aside>
      </section>
    </div>
  </main>
</div>
